import { useState, useCallback, useRef } from 'react';
import Keycloak from 'keycloak-js';
import { WebSocketMessage } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' 
    ? 'https://localhost:7292'
    : 'https://api.dotnethub.pl'
  );

interface UseWebSocketReturn {
  connected: boolean;
  messages: WebSocketMessage[];
  connect: (keycloak: Keycloak | null) => Promise<void>;
  disconnect: () => void;
}

export const useWebSocket = (): UseWebSocketReturn => {
  const [connected, setConnected] = useState<boolean>(false);
  const [messages, setMessages] = useState<WebSocketMessage[]>([]);
  const wsRef = useRef<WebSocket | null>(null);

  const checkMaintenanceMode = async (): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE_URL}/`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });

      if (response.status === 503) {
        setMessages(prev => [...prev, { 
          type: 'error', 
          text: '🔧 System is in maintenance mode. Please try again later.', 
          time: new Date() 
        }]);
        
        // Reload to show maintenance page
        setTimeout(() => window.location.reload(), 2000);
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  };

  const connect = useCallback(async (keycloak: Keycloak | null) => {
    if (!keycloak?.token) {
      console.error('No Keycloak token available for WebSocket connection');
      setMessages(prev => [...prev, { 
        type: 'error', 
        text: 'Authentication required. Please login.', 
        time: new Date() 
      }]);
      return;
    }

    // Check if token expires soon and refresh if needed
    const tokenExp = keycloak.tokenParsed?.exp || 0;
    const now = Date.now() / 1000;
    const timeLeft = tokenExp - now;
    
    console.log(`⏰ Token expires in ${Math.floor(timeLeft)} seconds`);
    
    if (timeLeft < 60) {
      console.log('⚠️  Token expires soon, refreshing...');
      try {
        const refreshed = await keycloak.updateToken(30);
        if (refreshed) {
          console.log('✅ Token refreshed successfully');
        } else {
          console.log('ℹ️  Token is still valid');
        }
      } catch (error) {
        console.error('❌ Failed to refresh token:', error);
        setMessages(prev => [...prev, { 
          type: 'error', 
          text: 'Failed to refresh token. Please logout and login again.', 
          time: new Date() 
        }]);
        return;
      }
    }

    // Check maintenance mode before connecting
    const isMaintenance = await checkMaintenanceMode();
    if (isMaintenance) return;

    try {
      const wsBaseUrl = API_BASE_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      const wsUrl = `${wsBaseUrl}/websockets`;
      const wsUrlWithToken = `${wsUrl}?access_token=${encodeURIComponent(keycloak.token)}`;
      
      console.log('🔌 Connecting to WebSocket...');
      console.log('📍 WebSocket URL:', wsUrl);
      console.log('🔑 Token length:', keycloak.token.length);
      console.log('👤 Username:', keycloak.tokenParsed?.preferred_username);
      console.log('⏰ Token expires:', new Date((keycloak.tokenParsed?.exp || 0) * 1000));
      
      const ws = new WebSocket(wsUrlWithToken);
      
      ws.onopen = () => {
        console.log('✅ WebSocket connected successfully');
        setConnected(true);
        setMessages(prev => [...prev, { 
          type: 'system', 
          text: `Connected to WebSocket`, 
          time: new Date() 
        }]);
      };

      ws.onmessage = (event) => {
        console.log('📨 RAW MESSAGE:', event.data);
        try {
          const data = JSON.parse(event.data);
          console.log('📨 Parsed WebSocket message:', data);
          
          // Ignore ping messages (keep-alive)
          if (data.type === 'ping') {
            console.log('💓 Received keep-alive ping');
            return;
          }
          
          setMessages(prev => [...prev, { 
            type: data.type || 'message', 
            text: JSON.stringify(data, null, 2), 
            time: new Date() 
          }]);
        } catch (error) {
          console.error('Failed to parse message:', error);
          setMessages(prev => [...prev, { 
            type: 'message', 
            text: event.data, 
            time: new Date() 
          }]);
        }
      };

      ws.onerror = async (error) => {
        console.error('❌ WebSocket error:', error);
        console.error('WebSocket readyState:', ws.readyState);
        
        // Check if it's maintenance mode
        const isMaintenance = await checkMaintenanceMode();
        if (!isMaintenance) {
          setMessages(prev => [...prev, { 
            type: 'error', 
            text: `WebSocket error. Check console for details.`, 
            time: new Date() 
          }]);
        }
      };

      ws.onclose = async (event) => {
        console.log('🔌 WebSocket disconnected');
        console.log('   Code:', event.code);
        console.log('   Reason:', event.reason || '(no reason provided)');
        console.log('   Was clean:', event.wasClean);
        setConnected(false);
        
        // Check if server is in maintenance
        if (event.code === 1006 || event.code === 1001) {
          const isMaintenance = await checkMaintenanceMode();
          if (isMaintenance) return;
        }
        
        let closeMessage = 'Disconnected from WebSocket';
        
        if (event.code === 1000) {
          closeMessage += ' (Normal closure)';
        } else if (event.code === 1006) {
          closeMessage += ' (Abnormal closure - connection lost)';
        } else if (event.code === 1008) {
          closeMessage += ' (Policy violation)';
        } else if (event.code === 401 || event.code === 403) {
          closeMessage += ' (Unauthorized)';
        } else if (event.reason) {
          closeMessage += ` (${event.reason})`;
        } else {
          closeMessage += ` (Code: ${event.code})`;
        }
        
        setMessages(prev => [...prev, { 
          type: 'system', 
          text: closeMessage, 
          time: new Date() 
        }]);
      };

      wsRef.current = ws;

    } catch (error) {
      console.error('❌ Failed to connect WebSocket:', error);
      
      // Check maintenance mode on connection failure
      const isMaintenance = await checkMaintenanceMode();
      if (!isMaintenance) {
        setMessages(prev => [...prev, { 
          type: 'error', 
          text: `Connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 
          time: new Date() 
        }]);
      }
    }
  }, []);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnecting');
      wsRef.current = null;
    }
  }, []);

  // Note: We don't need useEffect cleanup here because:
  // 1. disconnect() callback handles proper cleanup
  // 2. React Strict Mode would cause double cleanup in development
  // 3. User explicitly clicks disconnect button to close connection

  return {
    connected,
    messages,
    connect,
    disconnect
  };
};
